/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import { rgba } from 'polished';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChessQueenIcon, HeartRateIcon, LibraryIcon } from '@treams/treams-icons';
import { Button, Callout, Heading, Input, Label, Paragraph, Select } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';

import AssociatedIdentities from './AssociatedIdentities';

const Anchor = styled(Box)`
	color: ${(props) => props.theme.colors.primary};
	border-bottom: 1px solid ${(props) => rgba(props.theme.colors.primary, 0.25)};
	cursor: pointer;

	&:hover {
		background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
		border-bottom-color: transparent;

		&:active {
			background: ${(props) => rgba(props.theme.colors.primary, 0.25)};
		}
	}
`;

export default function SignIn(props: {
	create?: boolean;
	user?: graphql.User;
	identityProviders?: graphql.IdentityProvider[];
	onChange?: (data: { username: string; email: string; externalReferenceID?: string }) => void;
}) {
	const { t } = useTranslation();

	const workspace = useWorkspace();

	const [update] = graphql.useUpdateUserMutation();
	const [associate] = graphql.useEnsureAssociatedIdentitiesMutation();
	const [assign] = graphql.useAssignRoleMutation();
	const [revoke] = graphql.useRevokeRoleMutation();

	const [editing, setEditing] = useState(props.create ?? false);

	const [error, attempt, reset] = useErrorBoundary();

	const initialRole =
		props.user?.roles?.find((role) =>
			[
				'workspace-super-user',
				'workspace-learning-officer',
				'workspace-engagement-officer',
			].includes(role.role.id),
		)?.role.type ?? graphql.RoleType.Member;

	const formik = useFormik({
		initialValues: {
			username: props.user?.username!,
			email: props.user?.email!,
			associatedIdentities: props.user?.associatedIdentities?.map((identity) => ({
				identityProviderID: identity.identityProvider.id,
				name: identity.name,
			}))!,
			externalReferenceID: props.user?.externalReferenceID!,
			role: initialRole,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		formik.setValues({
			username: props.user?.username!,
			email: props.user?.email!,
			associatedIdentities: props.user?.associatedIdentities?.map((identity) => ({
				identityProviderID: identity.identityProvider.id,
				name: identity.name,
			}))!,
			externalReferenceID: props.user?.externalReferenceID!,
			role: initialRole,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!props.user, props.user?.id]);

	useEffect(() => {
		if (!props.user) {
			return;
		}

		props.onChange?.(formik.values);

		reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(formik.values)]);

	useEffect(() => {
		if (error && !editing) {
			setEditing(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!error, editing]);

	const [send] = graphql.useSendTemporaryPasswordMutation();

	const [sent, setSent] = useState(false);

	return (
		<FormikProvider value={formik}>
			<Flex alignItems="center" mb={3}>
				<Heading flexGrow={1} flexShrink={1} mr={2}>
					{t('settings.employees.authentication.title')}
				</Heading>
				{!props.create && (
					<Box flexGrow={0} flexShrink={0}>
						{props.user ? (
							editing ? (
								<Button
									type="primary"
									title={t('common.save')}
									onClick={attempt(async () => {
										if (!props.user) {
											return;
										}

										setEditing(false);
										await update({
											variables: {
												input: {
													id: props.user.id,
													email: formik.values.email!,
													firstName: props.user.firstName,
													lastName: props.user.lastName,
													groupID: props.user.group.id,
													locale: props.user.locale,
													username: formik.values.username!,
													activatedAt: props.user.activatedAt,
													deactivatedAt: props.user.deactivatedAt,
													externalReferenceID: formik.values.externalReferenceID,
												},
											},
										});
										const userID = props.user.id;

										await Promise.all(
											props.identityProviders?.map((provider) => {
												const names = formik.values.associatedIdentities
													.filter((identity) => identity.identityProviderID === provider.id)
													.map((identity) => identity.name);

												return associate({
													variables: {
														input: {
															userID,
															identityProviderID: provider.id,
															names,
														},
													},
												});
											}) ?? [],
										);

										if (formik.values.role === initialRole) {
											return;
										}

										if (formik.values.role === graphql.RoleType.Member) {
											await revoke({
												variables: {
													input: {
														userID: props.user.id,
														roleID: props.user.roles.find(
															(value) => value.role.type === initialRole,
														)!.id,
														targetType: graphql.RoleTargetType.Workspace,
														targetID: workspace.id,
													},
												},
											});
										} else {
											await assign({
												variables: {
													input: {
														userID: props.user.id,
														roleID: `workspace-${formik.values.role
															.replaceAll('_', '-')
															.toLowerCase()}`,
														targetType: graphql.RoleTargetType.Workspace,
														targetID: workspace.id,
														isIndirect: false,
													},
												},
											});
										}
									})}
								/>
							) : (
								<Button
									type="transparent"
									disabled={!props.user.viewerCanUpdate}
									title={t('common.edit')}
									onClick={() => setEditing(true)}
								/>
							)
						) : (
							<Button.Skeleton />
						)}
					</Box>
				)}
			</Flex>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error} />
				</Box>
			)}
			<Flex mb={2}>
				<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="30%">
					<Label mb={2}>{t('settings.employees.authentication.username')}:</Label>
					{props.user ? <Input.Formik name="username" disabled={!editing} /> : <Input.Skeleton />}
				</Box>
				<Box flexGrow={1} flexShrink={1} flexBasis="70%">
					<Label mb={2}>{t('settings.employees.authentication.email')}:</Label>
					{props.user ? <Input.Formik name="email" disabled={!editing} /> : <Input.Skeleton />}
					{!props.create && props.user && (
						<Flex mt={2}>
							{sent ? (
								<Paragraph>{t('settings.employees.authentication.sent')}</Paragraph>
							) : (
								<Anchor
									onClick={() => {
										setSent(true);
										send({ variables: { input: { userID: props.user!.id } } });
									}}
								>
									{t('settings.employees.authentication.resend')}
								</Anchor>
							)}
						</Flex>
					)}
				</Box>
			</Flex>
			<Flex mb={2}>
				{!props.create && (
					<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="50%">
						<Label mb={2}>{t('settings.employees.authentication.role')}:</Label>
						{props.user ? (
							<Select.Formik
								key={`${editing}`}
								name="role"
								search={false}
								options={[
									{
										value: graphql.RoleType.SuperUser,
										title: t('roles.workspace-super-user'),
										icon: <ChessQueenIcon />,
									},
									{
										value: graphql.RoleType.LearningOfficer,
										title: t('roles.workspace-learning-officer'),
										icon: <LibraryIcon />,
									},
									{
										value: graphql.RoleType.EngagementOfficer,
										title: t('roles.workspace-engagement-officer'),
										icon: <HeartRateIcon />,
									},
									undefined,
									{
										value: graphql.RoleType.Member,
										title: t('common.none'),
									},
								]}
								disabled={!editing}
							/>
						) : (
							<Input.Skeleton />
						)}
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.hris')}:</Label>
					{props.user ? (
						<Input.Formik key={`${editing}`} name="externalReferenceID" disabled={!editing} />
					) : (
						<Input.Skeleton />
					)}
				</Box>
			</Flex>
			{formik.values.role !== initialRole &&
				(formik.values.role === graphql.RoleType.SuperUser ? (
					<Callout color="manager">
						{t('settings.employees.authentication.roles.warning.expansion', {
							name: props.user?.firstName,
						})}
					</Callout>
				) : (
					initialRole === graphql.RoleType.SuperUser && (
						<Callout color="manager">
							{t('settings.employees.authentication.roles.warning.reduction', {
								name: props.user?.firstName,
							})}
						</Callout>
					)
				))}
			{props.user &&
				(props.identityProviders ?? []).length > 0 &&
				((formik.values.associatedIdentities ?? []).length > 0 || editing) && (
					<AssociatedIdentities
						readOnly={!editing}
						email={formik.values.email ?? ''}
						username={formik.values.username ?? ''}
						user={props.user}
						associatedIdentities={formik.values.associatedIdentities ?? []}
						setAssociatedIdentities={(identities) => {
							formik.setFieldValue('associatedIdentities', identities);
						}}
					/>
				)}
		</FormikProvider>
	);
}
